import React from 'react';
import { Text } from '../../Text';
import {
  TChannelResponseStatus,
  TChannelParticipant,
  TChannelUserParticipant,
} from '../../../api/messages';
import { Button } from '../../Button';
import { Icon } from '../../Icon';
import {
  MOBILE_HEADER_ICON_SIZE,
  mobileHeaderIconButtonClass,
} from '../../Header/styles';
import { commonStrings, messageStrings } from '../../../constants/strings';
import { useCurrentUser } from 'hooks';
import { EditLineFriendModal } from '../EditLineFriendModal';

type Props = {
  participants: TChannelParticipant[];
  loading?: boolean;
  onBack?: () => void;
  responseStatus?: TChannelResponseStatus | null;
  onChangeResponseStatus?: (status: TChannelResponseStatus) => void;
  isUpdatingResponseStatus?: boolean;
};

const isMaleParticipant = (
  participant: TChannelParticipant | undefined,
): participant is TChannelUserParticipant =>
  participant?.provider === 'MALE_APPLICATION';

export function ChatHeader({
  participants,
  loading = false,
  onBack,
  responseStatus,
  onChangeResponseStatus,
  isUpdatingResponseStatus = false,
}: Props) {
  const { data: currentUser } = useCurrentUser();
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [isMemoExpanded, setIsMemoExpanded] = React.useState(false);

  const backButton = onBack ? (
    <button
      type="button"
      onClick={onBack}
      aria-label={commonStrings.actions.backToList}
      className={`md:hidden -ml-2 flex ${mobileHeaderIconButtonClass} shrink-0 items-center justify-center rounded-full text-ink transition hover:bg-neutral-50`}
    >
      <Icon
        icon="chevronLeft"
        width={MOBILE_HEADER_ICON_SIZE}
        height={MOBILE_HEADER_ICON_SIZE}
      />
    </button>
  ) : null;

  // Filter participants to exclude current user
  const memberList = React.useMemo(
    () => participants.filter((item) => item.uuid !== currentUser?.uuid) || [],
    [participants, currentUser?.uuid],
  );

  const userMale = memberList[0];
  const maleParticipant = isMaleParticipant(userMale) ? userMale : undefined;
  const memo = maleParticipant?.memo?.trim() || '';

  // 1行に収まらず実際に省略された場合のみ「もっと見る」を出す。
  const memoTextRef = React.useRef<HTMLSpanElement>(null);
  const [isMemoOverflowing, setIsMemoOverflowing] = React.useState(false);

  // メモ内容が変わったら必ず折りたたみ直す。展開したまま別の（短い）メモに差し替わると
  // 計測がスキップされ、省略不要なのに「閉じる」が残り続けるため。
  React.useLayoutEffect(() => {
    setIsMemoExpanded(false);
  }, [memo]);

  React.useLayoutEffect(() => {
    // 展開中は折りたたみ時の計測結果を保持する（再計測すると省略が解けて誤判定になる）。
    if (isMemoExpanded) return;

    const measure = () => {
      const el = memoTextRef.current;
      setIsMemoOverflowing(el ? el.scrollHeight - el.clientHeight > 1 : false);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [memo, isMemoExpanded]);

  if (loading) {
    return (
      <div className="w-full p-4 border-b flex items-center gap-4">
        {backButton}
        <div className="animate-pulse bg-gray-300 h-14 w-14 rounded-full"></div>
        <div className="animate-pulse bg-gray-300 h-8 flex-1 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 border-b flex flex-wrap items-start gap-x-2 gap-y-3 sm:gap-x-4">
      <div className="flex min-w-0 flex-1 basis-48 items-start gap-2 sm:gap-4">
        {backButton}
        <div className="flex flex-col flex-1 min-w-0 gap-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <Text className="break-all" bold>
              {maleParticipant
                ? `${maleParticipant.name} (${maleParticipant.userLineId})`
                : userMale?.name}
            </Text>
            {memo && (
              <span className="inline-flex items-baseline gap-1 text-sm text-muted min-w-0">
                <span
                  ref={memoTextRef}
                  className={`break-all ${
                    isMemoExpanded ? 'whitespace-pre-wrap' : 'line-clamp-1'
                  }`}
                >
                  {memo}
                </span>
                {isMemoOverflowing && (
                  <button
                    type="button"
                    className="shrink-0 text-primary underline"
                    onClick={() => setIsMemoExpanded((prev) => !prev)}
                  >
                    {isMemoExpanded
                      ? commonStrings.actions.seeLess
                      : commonStrings.actions.seeMore}
                  </button>
                )}
              </span>
            )}
          </div>
        </div>
        {maleParticipant && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0"
            onClick={() => setIsEditOpen(true)}
          >
            {commonStrings.actions.edit}
          </Button>
        )}
      </div>
      {maleParticipant && onChangeResponseStatus && (
        <div
          role="group"
          aria-label={messageStrings.responseStatus.label}
          aria-busy={isUpdatingResponseStatus}
          className="ml-auto flex shrink-0 items-center gap-1.5"
        >
          {(['NEEDS_RESPONSE', 'RESPONDED', 'NO_ACTION_NEEDED'] as const).map(
            (status) => {
              const isSelected = status === responseStatus;
              return (
                <Button
                  key={status}
                  type="button"
                  size="sm"
                  variant="outline"
                  aria-pressed={isSelected}
                  disabled={isUpdatingResponseStatus}
                  onClick={() => {
                    if (!isSelected) onChangeResponseStatus(status);
                  }}
                  className="min-h-9 rounded-md px-2 hover:translate-y-0 aria-pressed:border-primary-700 aria-pressed:bg-primary-50 aria-pressed:text-primary-800"
                >
                  {isSelected && <Icon icon="check" className="mr-1 h-4 w-4" />}
                  {/* 未選択時はチェックと間隔の幅を左右に分け、中央揃えとボタン幅を保つ。 */}
                  <span className={isSelected ? undefined : 'px-2.5'}>
                    {messageStrings.responseStatus[status]}
                  </span>
                </Button>
              );
            },
          )}
        </div>
      )}
      {maleParticipant && (
        <EditLineFriendModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          userLineId={maleParticipant.userLineId}
        />
      )}
    </div>
  );
}
